import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * usePolling — visibility-aware polling hook.
 *
 * - Fires fetchFn on the given interval while the tab is visible.
 * - Pauses when the tab/page becomes hidden (document.hidden / pagehide).
 * - On return to visibility: fires an immediate fetch, then restarts the interval.
 * - Does NOT fire on mount (caller should have their own useEffect for initial load).
 *
 * @param {Function} fetchFn - async function to call on each tick
 * @param {number} interval - polling interval in ms (default 8000)
 * @param {boolean} enabled - whether polling is active
 * @returns {{ lastFetchAt: number }} - timestamp of last successful fetch start
 */
export function usePolling(fetchFn, interval = 8000, enabled = true) {
  const savedFn = useRef(fetchFn);
  const intervalRef = useRef(null);
  const [lastFetchAt, setLastFetchAt] = useState(() => Date.now());

  useEffect(() => { savedFn.current = fetchFn; }, [fetchFn]);

  const doFetch = useCallback(() => {
    setLastFetchAt(Date.now());
    savedFn.current();
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const start = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(doFetch, interval);
    };

    const stop = () => {
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    };

    const onVisChange = () => {
      if (document.hidden) {
        stop();
      } else {
        doFetch(); // immediate fetch on return
        start();
      }
    };

    // pageshow/pagehide for mobile Safari bfcache
    const onPageShow = () => { if (!document.hidden) { doFetch(); start(); } };
    const onPageHide = () => { stop(); };

    start();
    document.addEventListener('visibilitychange', onVisChange);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisChange);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [interval, enabled, doFetch]);

  return { lastFetchAt };
}

export function useLocalStorage(key, initialValue) {
  const prefixedKey = key.startsWith('foretour:') ? key : `foretour:${key}`;
  const [stored, setStored] = useStorageState(prefixedKey, initialValue);
  return [stored, setStored];
}

function useStorageState(key, initialValue) {
  const { useState: useS } = require('react');
  const [value, setValue] = useS(() => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch { return initialValue; }
  });

  const setStoredValue = useCallback((val) => {
    const newVal = typeof val === 'function' ? val(value) : val;
    setValue(newVal);
    try {
      localStorage.setItem(key, JSON.stringify(newVal));
    } catch (e) { console.error('localStorage write failed', e); }
  }, [key, value, setValue]);

  return [value, setStoredValue];
}

export function useAccessedCodes() {
  const [codes, setCodes] = useLocalStorage('foretour:accessedCodes', []);

  const addCode = useCallback((code) => {
    setCodes((prev) => {
      if (prev.includes(code)) return prev;
      return [...prev, code];
    });
  }, [setCodes]);

  const removeCode = useCallback((code) => {
    setCodes((prev) => prev.filter((c) => c !== code));
  }, [setCodes]);

  return { codes, addCode, removeCode };
}
